import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, FileStack, Calendar, Trash2, Pencil, Check, X } from "lucide-react";
import { templatesApi } from "@/lib/api";

// Phase 1.6 — Apply Template dialog.
// 1. Lists every saved template (with task/dep counts).
// 2. User picks one + provides anchor_date.
// 3. POST /plan-templates/:id/apply — backend creates new PlanTasks + deps
//    relative to the chosen anchor and snaps to working calendar.
// 4. Calls onApplied(resp) so parent can refresh plan in place.
export default function ApplyTemplateDialog({
  open,
  onOpenChange,
  projectId,
  defaultAnchorDate,
  isAdmin,
  onApplied,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [anchorDate, setAnchorDate] = useState(defaultAnchorDate || "");
  const [deletingId, setDeletingId] = useState(null);
  // Inline rename state — Jul 2026. Templates can now be renamed without
  // going through Save-as-Template + delete-old. Description edits are
  // out-of-scope for the first cut (keep the UI dead-simple).
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingRename, setSavingRename] = useState(false);

  const startRename = (tpl, e) => {
    e.stopPropagation();
    setEditingId(tpl.id);
    setEditingName(tpl.name || "");
  };
  const cancelRename = (e) => {
    if (e) e.stopPropagation();
    setEditingId(null);
    setEditingName("");
  };
  const saveRename = async (tpl, e) => {
    if (e) e.stopPropagation();
    const next = (editingName || "").trim();
    if (!next) {
      toast.error("Template name is required");
      return;
    }
    if (next === tpl.name) {
      cancelRename();
      return;
    }
    setSavingRename(true);
    try {
      const updated = await templatesApi.update(tpl.id, { name: next });
      setTemplates((prev) =>
        prev.map((t) => (t.id === tpl.id ? { ...t, ...updated } : t))
      );
      toast.success("Template renamed");
      cancelRename();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setSavingRename(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await templatesApi.list();
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setAnchorDate(defaultAnchorDate || "");
    load();
  }, [open, defaultAnchorDate]);

  const handleApply = async () => {
    if (!selectedId) {
      toast.error("Select a template to apply");
      return;
    }
    if (!anchorDate) {
      toast.error("Anchor date is required");
      return;
    }
    setApplying(true);
    try {
      const resp = await templatesApi.apply(selectedId, projectId, anchorDate);
      const count = resp?.applied_tasks?.length || 0;
      const depCount = resp?.applied_dependencies?.length || 0;
      toast.success(
        `Applied — ${count} task${count === 1 ? "" : "s"}${depCount ? ` + ${depCount} dep${depCount === 1 ? "" : "s"}` : ""}`,
      );
      onApplied?.(resp);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplying(false);
    }
  };

  const handleDelete = async (tpl, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    setDeletingId(tpl.id);
    try {
      await templatesApi.remove(tpl.id);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      if (selectedId === tpl.id) setSelectedId(null);
      toast.success("Template deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-[600px]"
        data-testid="apply-template-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <FileStack className="w-5 h-5 text-amber-500" />
            Apply plan template
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Pick a template and the anchor date. Tasks will be created at
            <span className="font-mono text-zinc-300"> anchor + offset</span>,
            snapped to your project&apos;s working calendar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Anchor date */}
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500" htmlFor="apply-anchor-date">
              <Calendar className="inline w-3 h-3 mr-1" />
              Anchor date (Day 0)
            </Label>
            <Input
              id="apply-anchor-date"
              data-testid="apply-anchor-date-input"
              type="date"
              value={anchorDate}
              onChange={(e) => setAnchorDate(e.target.value)}
              className="bg-zinc-950 border-zinc-800 font-mono"
            />
          </div>

          {/* Templates list */}
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Available templates</Label>
            <div
              className="rounded-sm border border-zinc-800 max-h-[280px] overflow-auto"
              data-testid="apply-templates-list"
            >
              {loading ? (
                <div className="py-10 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                </div>
              ) : templates.length === 0 ? (
                <div className="py-10 text-center text-xs text-zinc-500">
                  No templates yet. {isAdmin ? "Use \u201CSave as template\u201D from a Plan toolbar to create one." : "Ask an admin to create one."}
                </div>
              ) : (
                <ul className="divide-y divide-zinc-800">
                  {templates.map((tpl) => {
                    const isSelected = selectedId === tpl.id;
                    return (
                      <li
                        key={tpl.id}
                        data-testid={`apply-template-row-${tpl.id}`}
                        onClick={() => setSelectedId(tpl.id)}
                        className={`px-3 py-2.5 cursor-pointer transition-colors flex items-start gap-3 ${
                          isSelected
                            ? "bg-amber-500/10 border-l-2 border-l-amber-500"
                            : "hover:bg-zinc-900/60"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setSelectedId(tpl.id)}
                          className="mt-1 accent-amber-500"
                        />
                        <div className="flex-1 min-w-0">
                          {editingId === tpl.id ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                data-testid={`apply-template-rename-input-${tpl.id}`}
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  e.stopPropagation();
                                  if (e.key === "Enter") saveRename(tpl, e);
                                  if (e.key === "Escape") cancelRename(e);
                                }}
                                disabled={savingRename}
                                autoFocus
                                className="h-7 text-sm bg-zinc-950 border-zinc-700 focus-visible:ring-amber-500"
                              />
                              <button
                                type="button"
                                data-testid={`apply-template-rename-save-${tpl.id}`}
                                onClick={(e) => saveRename(tpl, e)}
                                disabled={savingRename}
                                className="text-emerald-400 hover:text-emerald-300 transition-colors p-1"
                                title="Save"
                              >
                                {savingRename ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Check className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                data-testid={`apply-template-rename-cancel-${tpl.id}`}
                                onClick={cancelRename}
                                disabled={savingRename}
                                className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-zinc-100 truncate">
                              {tpl.name}
                            </div>
                          )}
                          {tpl.description && editingId !== tpl.id && (
                            <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                              {tpl.description}
                            </div>
                          )}
                          <div className="text-[10px] font-mono text-zinc-600 mt-1">
                            {tpl.tasks?.length || 0} tasks · {tpl.dependencies?.length || 0} deps ·
                            anchor: {tpl.anchor_label || "Day 0"}
                          </div>
                        </div>
                        {isAdmin && editingId !== tpl.id && (
                          <div className="flex items-center gap-0.5">
                            <button
                              type="button"
                              data-testid={`apply-template-rename-${tpl.id}`}
                              onClick={(e) => startRename(tpl, e)}
                              className="text-zinc-600 hover:text-amber-400 transition-colors p-1"
                              title="Rename template"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              data-testid={`apply-template-delete-${tpl.id}`}
                              onClick={(e) => handleDelete(tpl, e)}
                              disabled={deletingId === tpl.id}
                              className="text-zinc-600 hover:text-rose-400 transition-colors p-1"
                              title="Delete template"
                            >
                              {deletingId === tpl.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-zinc-400"
            data-testid="apply-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={applying || !selectedId || !anchorDate}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="apply-template-btn"
          >
            {applying && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
            Apply template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
